import axios, { AxiosResponse } from 'axios';
import combineURLs from 'axios/lib/helpers/combineURLs';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// Actions
import { updateUser } from './userReducer';
import { updatePerson } from './peopleReducer';
import { makeSnack } from './snackbarReducer';

// Utils
import WebClientRequest from '../../core-data-service/WebClientRequest';
import getAccessTokenFromResponse from '../../../core/utils/getAccessTokenFromResponse';
import analytics, { identifiyUser } from '../../utils/analytics';

// Constants
import { ENV_DEVELOPMENT, ENV_INTEGRATION, ONE_MINUTE } from '../../../core/constants';

import initialState from '../initial/auth';
import appConfig from '../../../core/appConfig';
import { RootState } from '..';
import { SendCodeFormData } from '../../views/Authenticate/SendCodeForm';
import { sanitizeDate } from '../../../core/utils/datetimeUtils';
import { trimWhitespace } from '../../../core/utils/stringHelpers';
import { SendEmailCodeFormData } from '../../views/Lenox/DisabilityLogin';
import UserInterface from '../../types/UserInterface';


interface ISendCode {
  onSuccess?: ( response: AxiosResponse )=> void;
  data: {
    phone: string | null;
  };
}

interface IVerifyCode {
  onSuccess?: ( response: AxiosResponse )=> void;
  data: {
    code: string | null;
  };
}

interface ISendEmailCode {
  onSuccess?: ( response: AxiosResponse )=> void;
  onError?: ()=> void;
  data: {
    email: string | null;
    dob: string | null;
    lastname: string | null;
    grant_type: string | null;
    ssn: string | null;
  };
}

const sanitizeBlackRockData = ( data: ISendEmailCode['data']) => {
  if( !!data.dob ){
    data.dob = sanitizeDate( data.dob );
  }
  if( !!data.lastname ){
    data.lastname = trimWhitespace( data.lastname );
  }
  return data;
};

export const sendEmailCode = createAsyncThunk<
  WebClientRequest,
  ISendEmailCode,
  {state: RootState}
  >(
  /**
   * `type` string
   * https://redux-toolkit.js.org/api/createAsyncThunk#type
   */
    'auth/sendEmailCode',

    /**
   * sendEmailCode - `payloadCreator` callback
   * https://redux-toolkit.js.org/api/createAsyncThunk#payloadcreator
   */
    async({ data, onSuccess, onError }: ISendEmailCode, thunkAPI ) => {
      const url = '/v1/oauth/token';
      const access_token = thunkAPI.getState().auth.access_token;
      data.grant_type = 'blackrock';
      return WebClientRequest
        .post( url,sanitizeBlackRockData( data ))
        .then( response =>{

          // Extract access token from response...
          const thisToken = getAccessTokenFromResponse( response );

          // If access token changed during last request, update value in store
          if( thisToken !== access_token ){
            WebClientRequest.token = access_token;
          }
          response.data.email = data.email;
          onSuccess && onSuccess( response );
          return data;
        }).catch( error => {
          thunkAPI.dispatch( makeSnack({ message: 'The info you entered does not match our records.', theme: 'error' }));
          onError && onError();
          throw new Error( error );
        });
    },
  );


/**
 * THUNK - verifyEmailCode - use express server proxy
 */


export const verifyEmailCode = createAsyncThunk<
  WebClientRequest,
  IVerifyCode,
  {state: RootState}
  >(
  /**
   * `type` string
   * https://redux-toolkit.js.org/api/createAsyncThunk#type
   */
    'auth/verifyEmailCode',
    /**
   * VerifyEmailCode - `payloadCreator` callback
   * https://redux-toolkit.js.org/api/createAsyncThunk#payloadcreator
   */
    async({ data, onSuccess }: IVerifyCode, thunkAPI ) => {
      const url = combineURLs( appConfig.app.baseurl, '/api/verifyEmailCode' );
      const identifier = thunkAPI.getState().auth.sendEmailCode?.data?.email;
      const grant_type = 'verification_code';
      const method = 'email';
      if( !identifier ){
        throw new Error( '[auth/verifyEmailCode] Cannot find `data.email` in sendCode state' );
      }

      const codeData = {
        ...data,
        identifier,
        grant_type,
        method,
      };
      return axios
        .post( url, codeData )
        .then( response => {
          const access_token = response.data.access_token;
          WebClientRequest.token = access_token;
          thunkAPI.dispatch( hasAuthenticated( true ));
          //TODO: find out what how we want to track lenox users.
          onSuccess && onSuccess( response );
        })
        .catch( error => {
        // If incorrect code, "Incorrect verification code." red toast displays.
          const message = error?.response?.status === 400 ? 'Incorrect verification code.' : error.message;
          thunkAPI.dispatch( makeSnack({ message, theme: 'error' }));
          throw new Error( error );
        });
    },
  );
/**
 * THUNK - Send code to phone number to initiate 2-Factor authentication flow
 * https://redux-toolkit.js.org/api/createAsyncThunk
 */
export const sendCode = createAsyncThunk<
  WebClientRequest,
  ISendCode,
  {state: RootState}
>(

  /**
   * `type` string
   * https://redux-toolkit.js.org/api/createAsyncThunk#type
   */
  'auth/sendCode',

  /**
   * sendCode - `payloadCreator` callback
   * https://redux-toolkit.js.org/api/createAsyncThunk#payloadcreator
   */
  async({ data, onSuccess }: ISendCode, thunkAPI ) => {
    const url = '/v1/verification';
    const access_token = thunkAPI.getState().auth.access_token;
    return WebClientRequest
      .post( url, data )
      .then( response => {

        // Extract access token from response...
        const thisToken = getAccessTokenFromResponse( response );

        // If access token changed during last request, update value in store
        if( thisToken !== access_token ){
          WebClientRequest.token = access_token;
        }

        if( appConfig.app.env === ENV_DEVELOPMENT ||
            appConfig.app.deploymentEnv === ENV_INTEGRATION ){
          const num = response.data?.meta?.code || '******';
          console.log( // eslint-disable-line no-console
            `
            ┌──────────────────┐
            │                  │░░
            │   CODE: ${num}   │░░
            │                  │░░
            └──────────────────┘░░
              ░░░░░░░░░░░░░░░░░░░░

            ` );
        }

        onSuccess && onSuccess( response );

        // Return payload to action when fulfilled
        return data;
      });
  },
);


/**
 * THUNK - verifyCode - use express server proxy
 */
export const verifyCode = createAsyncThunk<
  WebClientRequest,
  IVerifyCode,
  {state: RootState}
>(
  'auth/verifyCode',

  async({ data: formData, onSuccess }: IVerifyCode, thunkAPI ) => {
    const url = combineURLs( appConfig.app.baseurl, '/api/verifyCode' );
    const phone = thunkAPI.getState().auth.sendCode?.data?.phone;

    if( !phone ){
      throw new Error( '[auth/verifyCode] Cannot find `data.phone` in sendCode state' );
    }

    const data = {
      ...formData,
      phone,
    };

    return axios
      .post( url, data )
      .then( response => {
        const access_token = response.data.access_token;
        const user = response.data.user as UserInterface;
        const person = response.data.user.person;

        WebClientRequest.token = access_token;
        thunkAPI.dispatch( updateUser( user ));
        thunkAPI.dispatch( updatePerson( person ));
        identifiyUser( user );
        const createdTime = ( new Date( user.created_at )).getTime();
        const now = Date.now();
        if( now - createdTime <= ONE_MINUTE ){
          analytics.track( 'NewRegisteredUser' );
        }
        onSuccess && onSuccess( response );
      })
      .catch( error => {
        // If incorrect code, "Incorrect verification code." red toast displays.
        const message = error?.response?.status === 400 ? 'Incorrect verification code.' : error.message;
        thunkAPI.dispatch( makeSnack({ message, theme: 'error' }));
        throw new Error( error );
      });
  },
);



/**
 * createSlice - https://redux-toolkit.js.org/api/createSlice
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,

  // standard reducer logic, with auto-generated action types per reducer
  reducers: {

    // store "hasAuthenticated" property
    hasAuthenticated( auth, action ){
      auth.hasAuthenticated = action.payload;
    },

    // store "hasCompletedOnboarding" property
    hasCompletedOnboarding( auth, action ){
      auth.hasCompletedOnboarding = action.payload;
    },

    // Clear the auth.sendCode storage
    resetSendCode( auth ){
      auth.sendCode.meta = initialState.sendCode.meta;
      auth.verifyCode.rejectedCount = 0;
    },

    // store "access_token" property
    storeAccessToken( auth, action ){
      auth.access_token = action.payload;
    },

  },

  /**
   * pending|fulfilled|rejected
   */
  extraReducers: builder => {

    /** sendCode */
    builder.addCase( sendCode.pending, ( auth, action ) => {
      const { arg, ...meta } = action.meta;
      auth.sendCode.meta = meta;
      auth.sendCode.data = null;
    });
    builder.addCase( sendCode.fulfilled, ( auth, action ) => {
      const { arg, ...meta } = action.meta;
      const response = action.payload as SendCodeFormData;
      auth.sendCode.meta = meta;
      auth.sendCode.data = response;
      auth.sendCode.updatedAt = new Date().toISOString();
    });
    builder.addCase( sendCode.rejected, ( auth, action ) => {
      const { arg, ...meta } = action.meta;
      auth.sendCode.meta = meta;
      auth.sendCode.data = null;
      auth.sendCode.errorMessage = action.error?.message;
      auth.sendCode.updatedAt = new Date().toISOString();
    });
    /** verifyCode */
    builder.addCase( verifyCode.pending, ( auth, action ) => {
      const { arg, ...meta } = action.meta;
      auth.verifyCode.meta = meta;
    });
    builder.addCase( verifyCode.fulfilled, ( auth, action ) => {
      const { arg, ...meta } = action.meta;
      delete auth.verifyCode.errorMsg;
      auth.verifyCode.meta = meta;
      auth.verifyCode.updatedAt = new Date().toISOString();
    });
    builder.addCase( verifyCode.rejected, ( auth, action ) => {
      const { arg, ...meta } = action.meta;
      auth.verifyCode.errorMsg = action.error?.message;
      auth.verifyCode.meta = meta;
      auth.verifyCode.updatedAt = new Date().toISOString();
      auth.verifyCode.rejectedCount++;
    });
    builder.addCase( sendEmailCode.pending, ( auth, action ) => {
      const { arg, ...meta } = action.meta;
      auth.sendEmailCode.meta = meta;
      auth.sendEmailCode.data = null;
    });
    builder.addCase( sendEmailCode.fulfilled, ( auth, action ) => {
      const { arg, ...meta } = action.meta;
      auth.sendEmailCode.meta = meta;
      auth.sendEmailCode.data = action.payload as SendEmailCodeFormData;
      auth.sendEmailCode.updatedAt = new Date().toISOString();
    });
    builder.addCase( sendEmailCode.rejected, ( auth, action ) => {
      const { arg, ...meta } = action.meta;
      auth.sendEmailCode.meta = meta;
      auth.sendEmailCode.data = null;
      auth.sendEmailCode.errorMessage = action.error?.message;
      auth.sendEmailCode.updatedAt = new Date().toISOString();
    });
  },
},
);
export const {
  hasAuthenticated,
  hasCompletedOnboarding,
  resetSendCode,
  storeAccessToken,
} = authSlice.actions;

export default authSlice.reducer;
